// ./src/utils/complianceSetup.ts
//
// WHAT A HOST MUST ASK BEFORE THEIR WAIVERS CAN ACTUALLY BE COMPLETED.
//
// ⚠⚠ A POLICY IS ATTACHED ON ONE PAGE AND MADE UNWORKABLE ON ANOTHER. Attaching a waiver takes
// seconds on the Compliance page; whether anyone can ever SIGN it is decided by the custom
// questions on a different page entirely. Nothing connects the two at the moment the host makes the
// mistake, and nothing fails afterwards — which is why both gaps below shipped and were found by
// reading data rather than by an error.
//
// ⚠ ONE IMPLEMENTATION, because the birthday half already had two. `eventDashboardWidgets` carried
// a copy with the comment "Same rule as the guest form's `policyNeedsBirthdate` and the compliance
// page" — three readings of one rule, in two apps. The public site's copy lives in
// `apps/events-web/components/rsvp/PolicyAcceptance.tsx` and cannot be imported here (separate app),
// so it stays a documented MIRROR; everything inside `apps/app` now reads this file.
/**
 * ⚠⚠ NO RUNTIME IMPORT FROM THE SERVICE LAYER, DELIBERATELY. This module is a pure rule, and a guard
 * has to be able to `import` it outside the browser — pulling in `RsvpQuestionService` drags in
 * `config/branding`, which reads `import.meta.env` and dies the moment anything but Vite loads it.
 * The type is erased; the one value is mirrored below.
 */
import type { RsvpQuestionInput } from "@/utils/api/RsvpQuestionService";

/** Mirrors `QUESTION_ASKED_ON.RSVP` — the guest form, which BOTH the RSVP and ticket rails read. */
const ASKED_ON_RSVP = "rsvp";

/** The event's age of majority when it declares none. Mirrors `DEFAULT_AGE_OF_MAJORITY`. */
export const DEFAULT_AGE_OF_MAJORITY = 18;

/** The shape both gaps read — whatever the caller's policy row looks like. */
export interface PolicyLike {
  minAge?: number | null;
  maxAge?: number | null;
  policyText?: string | null;
  /** #164 — 'sign' (a link to each adult / guardian) | 'checkbox' (ticked on the form). Absent = 'sign'. */
  acceptanceMethod?: string | null;
  requireSignature?: boolean | null;
  requireInitials?: boolean | null;
}

/** An attached policy link: the policy plus whether the event REQUIRES it. */
export interface PolicyLinkLike {
  policy?: PolicyLike | null;
  isRequired?: boolean | null;
}

/** A question row, as any of the three surfaces has it. */
export interface QuestionLike {
  /** Needed to offer "turn it back on" for a switched-off question (absent → that offer is not made). */
  id?: string | null;
  personField?: string | null;
  requiredScope?: string | null;
  isRequired?: boolean | null;
  status?: number | null;
}

const ageGated = (p?: PolicyLike | null): boolean => p?.minAge != null || p?.maxAge != null;

/**
 * Accepted by a LINK on its own signing page — a policy with a text, unless its host made it "a tick box is enough"
 * (#164). That is what makes age decide WHO receives the link, and what needs an adult's email.
 *
 * ⚠ MIRROR of the server's `policySignsByLink` (policyCapture.ts), which the public forms read as `selfSign`. A tick-box
 *   policy keyed on "has a text" here told the host to add a date-of-birth and an email question the guests never need.
 */
export const policySignsByLink = (p?: PolicyLike | null): boolean =>
  !!String(p?.policyText ?? "").trim() && p?.acceptanceMethod !== "checkbox";

const selfSign = policySignsByLink;

/**
 * What a guest does to accept a policy, for the host's badges (the Policies list, the Attach drawer, the Compliance
 * page) — ONE reading, so the three cannot disagree. A signature / initials only exist on the signing page, so a tick-box
 * policy never shows them even when its switches were left on from before (#164).
 */
export const policyAcceptanceSummary = (
  p?: PolicyLike | null,
): { hasText: boolean; byLink: boolean; signature: boolean; initials: boolean } => {
  const byLink = policySignsByLink(p);

  return {
    hasText: !!String(p?.policyText ?? "").trim(),
    byLink,
    signature: byLink && !!p?.requireSignature,
    initials: byLink && !!p?.requireInitials,
  };
};

/**
 * Whether a policy needs the guest's DATE OF BIRTH before it can be handled at all.
 *
 * ⚠ TWO independent reasons, and only the first is obvious:
 *   1. AGE-GATED — age decides whether the policy applies to this guest.
 *   2. SELF-SIGN — age decides WHO signs: a minor's guardian signs inline, an adult is emailed a
 *      link to sign it themselves.
 *
 * ⚠ MIRROR of `policyNeedsBirthdate` in `apps/events-web/components/rsvp/PolicyAcceptance.tsx`.
 * The guest form and the host's warning must agree, or the host is told everything is fine about a
 * form that refuses to submit.
 */
export const policyNeedsBirthdate = (p?: PolicyLike | null): boolean => ageGated(p) || selfSign(p);

/**
 * Whether a policy can land on an ADULT who then has to be EMAILED a link to sign it.
 *
 * ⚠⚠ THIS IS THE GAP THAT PRODUCES AN UNSIGNABLE WAIVER IN SILENCE (ROADMAP A5). A minor's waiver
 * is signed inline by the guardian who is standing at the form. An ADULT attendee is not: their
 * acceptance is minted PENDING and a link is EMAILED to them — and `persistPolicyAcceptances`
 * addresses it to `spec.email || spec.phone || null`, while `TicketOrderCore` only sends when there
 * is an address. With none, **nothing is ever sent**: the token exists in the database and nobody
 * can reach it. The buyer cannot open it either (`waiverDocumentAccess` answers NONE), so the
 * document is unreachable by every party to it until a host reissues it by hand.
 *
 * ⚠ An attendee's email exists ONLY as an answer to a question the host mapped to the person's
 * email field. So "can this waiver ever be signed?" is decided by whether somebody remembered to
 * add a question — which is what the warnings built on this are for.
 *
 * ⚠ A policy capped BELOW the age of majority is minor-only: its guardian signs inline and no adult
 * is ever emailed, so it does not need this.
 */
export const policyNeedsAdultEmail = (p?: PolicyLike | null, ageOfMajority = DEFAULT_AGE_OF_MAJORITY): boolean => {
  if (!selfSign(p)) {
    return false;
  }

  return p?.maxAge == null || Number(p.maxAge) >= ageOfMajority;
};

/** Mirrors `STATUSES.ACTIVE` (@sdflc/utils) — kept as a value so this module stays import-free; the guard pins it. */
export const QUESTION_STATUS_ACTIVE = 100;

/**
 * Is this question actually ASKED of guests? ⚠ A turned-off (disabled) question is never shown on the guest form, so it
 * supplies nothing — counting it hid a real gap (a turned-off date of birth reported "fine" while every reply to a
 * required age-gated waiver was refused). A row with NO status is a caller that already filtered to live questions.
 * ⓘ The callers differ: the Questions page and the wizard pass active AND disabled questions, the dashboard active only —
 *   deciding it HERE is what makes them agree.
 */
const isAsked = (q: QuestionLike | null | undefined): boolean => q?.status == null || q.status === QUESTION_STATUS_ACTIVE;

/** Is the question's answer REQUIRED of adults (or of everyone)? */
const requiredOfAdults = (q: QuestionLike | null | undefined): boolean => {
  // ⚠ The legacy boolean is the fallback `isAnswerRequiredFor` uses; keep the two in step.
  const scope = q?.requiredScope ?? (q?.isRequired ? "all" : "none");

  return scope === "all" || scope === "adults";
};

/** A question that supplies `personField`, required of adults (or of everyone). */
const suppliesForAdults = (questions: QuestionLike[], personField: string): boolean =>
  questions.some((q) => q?.personField === personField && isAsked(q) && requiredOfAdults(q));

/**
 * The SWITCHED-OFF question that would close this gap if turned back on, or null. Switching it back on is the better
 * fix than adding a second one (owner, 2026-10-01): the host already wrote it. For the adult email, one already required
 * of adults is preferred — turning that on closes the gap outright, where an optional one only moves it to "make it
 * required".
 */
const turnedOffFor = (questions: QuestionLike[], personField: string, needsRequired: boolean): string | null => {
  const off = questions.filter((q) => q?.personField === personField && !isAsked(q) && !!q?.id);
  const best = (needsRequired ? off.find(requiredOfAdults) : undefined) ?? off[0];

  return best?.id ?? null;
};

/** Whether the question is asked at all, required or not. */
const supplies = (questions: QuestionLike[], personField: string): boolean =>
  questions.some((q) => q?.personField === personField && isAsked(q));

export interface ComplianceGap {
  /** How many attached policies are affected. */
  affected: number;
  /** True when at least one of them is REQUIRED of the guest. */
  blocking: boolean;
  /** The question exists but is not required of adults — a different sentence, and a different fix. */
  existsButOptional: boolean;
  /**
   * A SWITCHED-OFF question that would close the gap if turned back on — offered instead of adding a new one. Null when
   * there is none, or when a live question already exists (then the fix is to make it required).
   */
  turnedOffQuestionId: string | null;
}

export interface ComplianceGaps {
  /** No date-of-birth question, so an age-gated or signable policy cannot be handled at all. */
  birthday: ComplianceGap | null;
  /** No adult email question, so an adult's waiver is minted and never sent. */
  adultEmail: ComplianceGap | null;
}

/**
 * The setup gaps on one event. Returns `null` per gap when nothing is wrong, so a caller renders
 * nothing at all rather than an empty alert.
 */
export const complianceSetupGaps = (args: {
  links: PolicyLinkLike[];
  questions: QuestionLike[];
  ageOfMajority?: number | null;
}): ComplianceGaps => {
  const links = Array.isArray(args.links) ? args.links : [];
  const questions = Array.isArray(args.questions) ? args.questions : [];
  const majority = Number(args.ageOfMajority) || DEFAULT_AGE_OF_MAJORITY;

  const gap = (needs: (p?: PolicyLike | null) => boolean, personField: string, needsRequired: boolean): ComplianceGap | null => {
    // ⚠ The date-of-birth question only has to EXIST — an optional answer still lets a guest supply
    // an age. The adult email has to be REQUIRED of adults, because an optional one left blank
    // produces exactly the unsignable waiver this is about.
    if (needsRequired ? suppliesForAdults(questions, personField) : supplies(questions, personField)) {
      return null;
    }

    const affected = links.filter((l) => needs(l?.policy));

    if (affected.length === 0) {
      return null;
    }

    const existsButOptional = needsRequired && supplies(questions, personField);

    return {
      affected: affected.length,
      blocking: affected.some((l) => !!l?.isRequired),
      existsButOptional,
      turnedOffQuestionId: existsButOptional ? null : turnedOffFor(questions, personField, needsRequired),
    };
  };

  return {
    birthday: gap(policyNeedsBirthdate, "birthday", false),
    adultEmail: gap((p) => policyNeedsAdultEmail(p, majority), "email", true),
  };
};

/**
 * The question we offer to create for the host — "add it for me" (owner, 2026-09-25).
 *
 * ⚠ EVERY VALUE HERE IS LOAD-BEARING, not defaults:
 *   • `personField` is what makes the answer reach the person and the waiver's address. Without it
 *     the host has a question that collects an email nothing ever uses.
 *   • `requiredScope: 'adults'` — NOT `'all'`. A required contact question on a CHILD's row is
 *     actively harmful: the person filling the form has no address for the child, enters their own,
 *     and `ensurePerson` matches on email so the child resolves to the adult's contact and their
 *     ticket, answers and minor waiver land on the adult's guest row.
 *   • `askedOn: 'rsvp'` is the guest form, which BOTH rails read — the ticket checkout renders the
 *     same per-attendee questions.
 */
export const ADULT_EMAIL_QUESTION = (eventId: string): { askedOn: string; params: RsvpQuestionInput } => ({
  askedOn: ASKED_ON_RSVP,
  params: {
    eventId,
    questionType: "single_value",
    valueType: "email",
    personField: "email",
    title: "Email address",
    helpText: "We send each adult their own copy of anything they need to sign.",
    requiredScope: "adults",
  },
});
